/**
 * Word display component with animations
 * Shows vocabulary word and definition with visual effects
 */
import { Card } from './ui/card';
import { Button } from './ui/button';
import { LoadingSpinner } from './ui/loading-spinner';
import { Sparkles, Smartphone } from 'lucide-react';
import type { VocabularyEntry, PermissionState } from '../types';

interface WordDisplayProps {
  word: VocabularyEntry | null;
  isAnimating: boolean;
  isPending: boolean;
  isSupported: boolean;
  permissionState: PermissionState;
  hasShownPermissionPrompt: boolean;
  onRequestPermission: () => void;
  onNewWord: () => void;
}

export function WordDisplay({
  word,
  isAnimating,
  isPending,
  isSupported,
  permissionState,
  hasShownPermissionPrompt,
  onRequestPermission,
  onNewWord,
}: WordDisplayProps) {
  return (
    <main className="flex-1 flex flex-col items-center justify-center px-4 pb-20">
      {/* Show permission prompt card on first visit if motion is supported */}
      {isSupported && permissionState === 'prompt' && !hasShownPermissionPrompt && (
        <Card className="mb-8 p-6 max-w-md w-full bg-gradient-to-br from-primary/10 to-accent/10 border-primary/20">
          <div className="flex flex-col items-center text-center gap-4">
            <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center">
              <Smartphone className="w-8 h-8 text-primary" />
            </div>
            <div>
              <h3 className="font-semibold text-lg mb-2">Enable Motion Detection</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Allow motion sensor access to use shake gesture for new words
              </p>
            </div>
            <Button onClick={onRequestPermission} className="w-full">
              Enable Motion Detection
            </Button>
          </div>
        </Card>
      )}

      {/* Main word card with animation states */}
      <div className="w-full max-w-2xl">
        <Card
          className={`p-8 md:p-12 bg-gradient-to-br from-card via-card to-accent/5 border-2 transition-all duration-500 ${
            isAnimating
              ? 'scale-95 opacity-50 rotate-1'  // Animating state
              : 'scale-100 opacity-100 rotate-0 shadow-2xl'  // Resting state
          }`}
        >
          {word ? (
            <div className="text-center space-y-6">
              <div className="flex items-center justify-center gap-2 mb-4">
                <Sparkles className="w-5 h-5 text-accent animate-pulse" />
                <span className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                  Current Word
                </span>
                <Sparkles className="w-5 h-5 text-accent animate-pulse" />
              </div>

              <h2 className="text-5xl md:text-7xl font-bold bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent animate-in fade-in slide-in-from-bottom-4 duration-500">
                {word.word}
              </h2>

              <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent" />

              <p className="text-lg md:text-xl text-foreground/80 leading-relaxed animate-in fade-in slide-in-from-bottom-4 duration-700">
                {word.definition}
              </p>
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-muted-foreground">No words available yet</p>
            </div>
          )}
        </Card>

        {/* Action button and status messages */}
        <div className="mt-8 text-center space-y-4">
          <Button
            onClick={onNewWord}
            disabled={isPending || isAnimating}
            size="lg"
            className="px-8 py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all"
          >
            {isPending || isAnimating ? (
              <>
                <LoadingSpinner size="sm" className="border-primary-foreground mr-2" />
                Loading...
              </>
            ) : (
              <>
                <Sparkles className="w-5 h-5 mr-2" />
                Get New Word
              </>
            )}
          </Button>

          {isSupported && permissionState === 'granted' && (
            <p className="text-sm text-muted-foreground">
              Shake your device or tap the button for a new word
            </p>
          )}

          {isSupported && permissionState === 'denied' && (
            <p className="text-xs text-muted-foreground">
              Motion sensor access denied. Use the button to get new words.
            </p>
          )}

          {!isSupported && (
            <p className="text-xs text-muted-foreground">
              Motion detection not available on this device
            </p>
          )}
        </div>
      </div>
    </main>
  );
}

